import { useParams } from 'react-router-dom'
import { useUser, useUserContext } from '../../../../context/UserProvider'
import { useQuery } from 'react-query'
import { fetchCarByAccountNo } from '../../../../api/api'
import FleetReportDetailsTable from '../components/FleetReportDetailsTable'
import { ExcelExportButton } from '../components/ExcelExporter'
import { excelFleetReportDetailsColumns } from '../components/FleetReportDetailsTable/excelUtils'
import { useEffect, useState } from 'react'
import { dateToValue, monthInputValue, valueToDate } from '../../../../utils/helpers/helpers.reports'
import type { Car } from '../../../../types'
import AccountInfo from '../components/FleetReportDetailsTable/AccountInfo'
import { fetchFleetDetailsReport, fetchReportTermLimits } from '../../../../api/newApi'
import ImprovedDatePicker from '../../../../components/fields/ImprovedDatePicker'

const clampMonth = (value: string, min: string, max: string) => {
  if (value < min) return min
  if (value > max) return max
  return value
}

const monthToTerm = (month: string) => `${month}-01`

const FleetReportDetails = () => {
  const user = useUser()
  const { chosenCompany: company, fleetReportTerm, consolidateInvoice } = useUserContext() as any

  const { account } = useParams()
  const [term, setTerm] = useState(() => localStorage.getItem('fleetReportTerm') || fleetReportTerm || '')

  const companyId = company?.id
  const contactId = user?.user?.id
  const consolidateCode = consolidateInvoice?.ConsolidateCode || '_ALL_'

  const { data: termLimits } = useQuery(
    ['fleetReportTermLimits', companyId, contactId],
    () => fetchReportTermLimits({ companyId, contactId, type: 'fleet' }),
    {
      enabled: !!companyId && !!contactId,
    },
  ) as { data?: { oldest: string; newest: string } }

  const oldestMonth = termLimits?.oldest ? monthInputValue(termLimits.oldest) : undefined
  const newestMonth = termLimits?.newest ? monthInputValue(termLimits.newest) : undefined

  useEffect(() => {
    if (!oldestMonth || !newestMonth) return

    const currentMonth = term ? monthInputValue(term) : newestMonth
    const nextTerm = monthToTerm(clampMonth(currentMonth, oldestMonth, newestMonth))

    if (nextTerm !== term) {
      setTerm(nextTerm)
      return
    }

    localStorage.setItem('fleetReportTerm', nextTerm)
  }, [newestMonth, oldestMonth, term])

  const { data: car } = useQuery(
    ['carByAccountNo', contactId, companyId, account],
    () => fetchCarByAccountNo(contactId, companyId, account),
    { enabled: !!contactId && !!companyId && !!account },
  ) as { data: Car }

  const { data: reportResponse, isLoading } = useQuery(
    ['fleetReportDetails', companyId, account, term, consolidateCode],
    () =>
      fetchFleetDetailsReport({
        term,
        companyId,
        contactId,
        accountNo: account,
        consolidatedAgreementID: consolidateCode,
      }),
    { enabled: !!companyId && !!contactId && !!term },
  )

  const items = (reportResponse ? ('items' in reportResponse ? reportResponse.items : []) : []) as any[]

  const columns = excelFleetReportDetailsColumns()
  const config = {
    columns,
    filename: `Fleet_Detaljer_${account}_${term}.xlsx`,
    sheetName: `Fleet_Detaljer_${account}`,
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col gap-2">
            <span className="text-xl font-medium text-primaryText">Flåtedetaljer</span>
            <AccountInfo car={car} />
          </div>
          <div className="md:hidden">
            <ExcelExportButton data={items} config={config} />
          </div>
        </div>
        <div className="flex flex-row items-end gap-2 w-full md:w-auto md:self-end">
          <div className="flex flex-col gap-1 text-sm text-secondaryText flex-1 md:flex-none">
            <label htmlFor="fleet-report-details-term">Termin</label>
            <ImprovedDatePicker
              id="fleet-report-details-term"
              selected={valueToDate(term ? monthInputValue(term) : newestMonth || '', 'month')}
              language="nb"
              type="month"
              minDate={oldestMonth ? valueToDate(oldestMonth, 'month') : undefined}
              maxDate={newestMonth ? valueToDate(newestMonth, 'month') : undefined}
              disabled={!oldestMonth || !newestMonth}
              onChange={(date) => date && setTerm(monthToTerm(dateToValue(date, 'month')))}
            />
          </div>
          <div className="hidden md:block self-end">
            <ExcelExportButton className="h-11" data={items} config={config} />
          </div>
        </div>
      </div>
      <FleetReportDetailsTable isLoading={isLoading} data={items} />
    </div>
  )
}

export default FleetReportDetails
